import { z } from 'zod';
import { CANNON_CATALOG, shipCatalogEntry } from '../catalog';
import {
  type CannonRarity,
  cannonRaritySchema,
  isCannonRarity,
  type Rarity,
  raritySchema,
} from '../rarity';
import {
  findItemDefinition,
  type ItemDefinition,
  isItemLevelAllowed,
  itemDefinitionIdSchema,
} from './definitions';
import {
  EMPTY_JEWEL_SOCKET_PROFILE,
  type JewelSocketProfile,
  jewelSocketProfileSchema,
} from './jewel-profile';

/**
 * The equipped loadout, resolved from real owned instances (ADR 0005).
 *
 * A RESOLVED loadout is the only thing the hunt is ever configured from. It is
 * produced here, from two instance rows plus the authoritative catalog, and
 * never from a value the browser sent, a column somebody cached, or a global
 * constant. Every numeric fact - damage bonus, projectiles per cannon - is
 * recomputed from the catalog at resolution time, so a stale row cannot pay
 * out at a rate that no longer exists.
 *
 * Resolution is FAIL-CLOSED: an unknown definition, a disabled one, the wrong
 * category, an impossible cannon rarity, a level past the cap, the wrong
 * owner, a reserved instance, a consumed or retired one, or a missing slot all
 * produce a typed failure. None of them falls back to a default loadout -
 * that fallback is exactly how a broken vault would keep quietly paying.
 */

/**
 * **Os três espaços de carta**, nomeados antes de entrarem na lista geral.
 *
 * Uma constante própria porque três coisas precisam dela sem precisarem da
 * lista inteira: o pedido de equipar, que nomeia UM dos três; a leitura do
 * estado, que devolve exactamente três; e o banco, cujo `CHECK` de vocabulário
 * os enumera ao lado dos outros nove.
 */
export const CARD_SLOTS = ['card_slot_1', 'card_slot_2', 'card_slot_3'] as const;
export type CardSlot = (typeof CARD_SLOTS)[number];
export const cardSlotSchema = z.enum(CARD_SLOTS);

/** Três. O contrato aprovado diz três, e é este número que a leitura devolve. */
export const CARD_SLOT_COUNT = CARD_SLOTS.length;

export function isCardSlot(value: string): value is CardSlot {
  return (CARD_SLOTS as readonly string[]).includes(value);
}

/**
 * Slots that exist today.
 *
 * `ship` and `cannon` are equipment the hunt is CONFIGURED from and cannot run
 * without - the resolver below fails closed on either. `pet` is a third slot
 * of a different kind: optional, at most one, and a hunt runs perfectly well
 * with it empty. Artifacts are deliberately absent and always will be - they
 * have no slot, and every copy an account owns contributes at once.
 */
export const LOADOUT_SLOTS = [
  'ship',
  'cannon',
  'pet',
  /**
   * The five energy-system slots (prompt 7A.1).
   *
   * Optional like `pet` and unlike the first two: a hunt runs perfectly well
   * with every one of them empty, which is what every account has today. They
   * are listed here so `loadout_changes` can record which one moved, and they
   * are resolved by `@kenocyber/shared`'s equipment module rather than by the
   * resolver below - a component is not something the hunt is CONFIGURED from,
   * it is something that adds to what the hunt already is.
   */
  'equipment_core',
  'equipment_chamber',
  'equipment_distributor',
  'equipment_conduits',
  'equipment_receptor',
  /**
   * The cosmetic skin, at most one per account (ADR 0036).
   *
   * Optional like the pet and the five components, and listed here for the same
   * reason: `loadout_changes` records which slot moved, and a skin moving is a
   * change the append-only history has to be able to name.
   *
   * It IS resolved below, and it is resolved differently from the other two.
   *
   * This comment used to say the opposite, and the reasoning was sound while it
   * was true: a skin configures nothing, so putting it in the resolution would
   * promise an effect that does not exist. What changed is that the hunt now
   * has to DRAW it, and the only honest source for "which copy is worn" is the
   * authoritative loadout - the alternative was the interface telling the game
   * what to render, which is the shape every other slot already refuses.
   *
   * So the skin travels here as PRESENTATION IDENTITY and nothing else: an
   * opaque instance id and a content definition id. It still grants no
   * attribute, still carries no socket into any calculation and still reaches
   * no simulation. `resolveLoadout` proves that structurally - the skin is
   * resolved after the numbers are decided and cannot feed any of them.
   *
   * And it fails OPEN where ship and cannon fail closed. A broken ship must
   * stop a hunt, because a hunt configured from a broken ship would pay out
   * wrongly. A broken skin must not: the worst honest outcome is the normal
   * hull, and refusing to start a run over a cosmetic would be a far worse
   * bargain than drawing the ship the player already owns.
   */
  'skin',
  /**
   * **Os três espaços de CARTA** (gate V28H.D).
   *
   * Três, e não um contador: `loadout_changes.slot` é um vocabulário fechado e
   * tem de conseguir nomear qual dos três se mexeu, e uma coluna por espaço é o
   * precedente canónico desta tabela desde os cinco componentes de energia.
   *
   * Os três são INTERCAMBIÁVEIS. Um Núcleo de Energia só entra no espaço do seu
   * componente; uma carta entra em qualquer um dos três, e a ordem existe para a
   * interface e para os atalhos que um gate futuro vai ligar - nunca para o
   * efeito.
   *
   * Como a skin, uma carta não entra em `ResolvedLoadout` neste gate: ela ainda
   * não configura nada, não tem cooldown em simulação e não tem habilidade. O
   * que este gate persiste é a ESCOLHA; a sessão de caça só a congelará quando
   * houver alguma coisa para congelar.
   */
  ...CARD_SLOTS,
] as const;
export type LoadoutSlot = (typeof LOADOUT_SLOTS)[number];
export const loadoutSlotSchema = z.enum(LOADOUT_SLOTS);

/** Why a loadout could not be resolved. Stable strings, safe to log. */
export const LOADOUT_FAILURES = [
  'slot_missing',
  'definition_unknown',
  'definition_disabled',
  'wrong_category',
  'invalid_cannon_rarity',
  'invalid_level',
  'wrong_owner',
  'instance_reserved',
  'instance_not_owned',
] as const;
export type LoadoutFailure = (typeof LOADOUT_FAILURES)[number];

/** One equipped instance, as the resolver receives it from the database. */
export interface EquippedInstanceInput {
  readonly instanceId: string;
  readonly definitionId: string;
  readonly ownerAccountId: string;
  readonly level: number;
  /** `owned` is the only state a usable item can be in. */
  readonly state: string;
  /** True when an active reservation holds it. */
  readonly reserved: boolean;
}

export interface ResolveLoadoutInput {
  readonly accountId: string;
  readonly ship: EquippedInstanceInput | null;
  readonly cannon: EquippedInstanceInput | null;
  /**
   * Corações Primordiais incorporados na nave equipada (gate V28F.B).
   *
   * Um parâmetro e não uma leitura: este módulo é neutro de plataforma e não
   * sabe o que é uma tabela. Quem resolve o loadout lê
   * `ship_instances.primordial_hearts` e passa o número, exatamente como já
   * passa as linhas das instâncias.
   *
   * Omitido é zero, e o zero é exato: uma nave sem linha de detalhe não tem
   * corações nenhuns.
   */
  readonly primordialHeartCount?: bigint;
  /**
   * As joias encaixadas na skin EQUIPADA (gate V28G.A).
   *
   * Um parâmetro e não uma leitura, pela mesma razão que a contagem de corações:
   * este módulo é neutro de plataforma e não sabe o que é uma tabela. Quem
   * resolve o loadout lê a ocupação e passa o perfil já agregado.
   *
   * Omitido é o perfil VAZIO, e o vazio é exato: um chamador que não resolve
   * joias descreve uma conta sem joias encaixadas, que é o estado de todas elas
   * hoje.
   */
  readonly jewels?: JewelSocketProfile;
  /** The worn skin's row, or null. Optional: absent means no skin. */
  readonly skin?: EquippedInstanceInput | null;
  /**
   * Which catalogue decides what a `definitionId` means (gate V23.1D).
   *
   * ## Why this is a parameter and not a global
   *
   * Because three different pieces of code answered this question three
   * different ways, and only one of them could be told the answer.
   * `InventoryService` already took an injectable `definitions` list;
   * `SkinService` read the module-level `SKIN_DEFINITIONS`; and this resolver
   * read the module-level `findItemDefinition`. A harness that injected a test
   * catalogue therefore got a vault that would happily EQUIP a test skin and a
   * resolver that then answered `null` for it - so the positive path was
   * unreachable in a browser, and the reason was invisible.
   *
   * Omitted, it is the real catalogue. Production passes nothing and behaves
   * exactly as before; a test composition passes its own effective catalogue to
   * every consumer at once, and the three agree by construction.
   */
  readonly lookup?: (definitionId: string) => ItemDefinition | null;
}

export interface ResolvedShip {
  readonly instanceId: string;
  readonly definitionId: string;
  /** 1-based index into the ship catalog; decides which PNG is drawn. */
  readonly shipIndex: number;
  readonly rarity: Rarity;
  readonly level: number;
  /** Recomputed from the catalog, in basis points. Never a stored value. */
  readonly damageBonusBps: number;
  readonly contentVersion: number;
  /**
   * Corações Primordiais incorporados nesta cópia, exato (gate V28F.B).
   *
   * Lido de `ship_instances.primordial_hearts`, que é a fonte única.
   *
   * ## Porque é uma STRING e não um `bigint`, aqui
   *
   * Porque este loadout resolvido é PERSISTIDO como JSON na sessão e lido de
   * volta por `parseResolvedLoadout`. A forma resolvida e a forma do fio têm de
   * ser a mesma coisa, ou a sessão congela um objeto que não consegue reler - e
   * um `bigint` nem sequer sobrevive a `JSON.stringify`.
   *
   * Quem precisa do número exato converte-o onde o usa: a configuração da caça
   * e a impressão digital do loadout. A conversão é visível nos dois sítios, que
   * é melhor do que uma serialização especial escondida aqui.
   *
   * Um `number` não serviria em forma nenhuma: a contagem não tem tecto de jogo
   * e perderia dígitos acima de 2^53.
   */
  readonly primordialHeartCount: string;
}

export interface ResolvedCannon {
  readonly instanceId: string;
  readonly definitionId: string;
  /** Narrow by construction: a Common cannon cannot be resolved. */
  readonly rarity: CannonRarity;
  readonly level: number;
  /** How many cannons the level mounts. */
  readonly cannonCount: number;
  /** How many projectiles each of them fires; decided by the rarity. */
  readonly projectilesPerCannon: number;
  readonly contentVersion: number;
}

export interface ResolvedSkin {
  readonly instanceId: string;
  readonly definitionId: string;
}

export interface ResolvedLoadout {
  readonly ship: ResolvedShip;
  readonly cannon: ResolvedCannon;
  /** The worn skin, or null. Presentation identity only. */
  readonly skin: ResolvedSkin | null;
  /** As joias encaixadas na skin equipada, agregadas (gate V28G.A). */
  readonly jewels: JewelSocketProfile;
}

export type LoadoutResolution =
  | { readonly ok: true; readonly loadout: ResolvedLoadout }
  | { readonly ok: false; readonly slot: LoadoutSlot; readonly reason: LoadoutFailure };

/**
 * Cannons mounted at a level: level `L` means `L` cannons, which is also what
 * the formation geometry produces (odd levels add one on the axis, every rung
 * adds a mirrored pair).
 *
 * Mirrors `cannonCountForLevel` in `@kenocyber/game-domain`, which cannot be
 * imported here - `shared` must not depend on `game-domain`. A cross-package
 * test compares this against the real `cannonFormation` at every legal level.
 */
export function cannonCountForLoadoutLevel(level: number): number {
  return level;
}

function fail(slot: LoadoutSlot, reason: LoadoutFailure): LoadoutResolution {
  return { ok: false, slot, reason };
}

/** Checks every fact that is true of ANY usable equipped instance. */
function validateCommon(
  slot: LoadoutSlot,
  accountId: string,
  input: EquippedInstanceInput,
  lookup: (definitionId: string) => ItemDefinition | null,
): { definition: ItemDefinition } | LoadoutResolution {
  if (input.ownerAccountId !== accountId) {
    return fail(slot, 'wrong_owner');
  }
  if (input.state !== 'owned') {
    return fail(slot, 'instance_not_owned');
  }
  if (input.reserved) {
    return fail(slot, 'instance_reserved');
  }
  const definition = lookup(input.definitionId);
  if (!definition) {
    return fail(slot, 'definition_unknown');
  }
  if (!definition.enabled) {
    return fail(slot, 'definition_disabled');
  }
  if (!isItemLevelAllowed(definition, input.level)) {
    return fail(slot, 'invalid_level');
  }
  return { definition };
}

function isResolution(value: unknown): value is LoadoutResolution {
  return typeof value === 'object' && value !== null && 'ok' in value;
}

/**
 * Turns two equipped instances into the numbers the hunt runs on.
 *
 * Pure and deterministic: the server calls it after reading the rows, and the
 * result is what gets frozen into the session.
 */
export function resolveLoadout(input: ResolveLoadoutInput): LoadoutResolution {
  if (!input.ship) {
    return fail('ship', 'slot_missing');
  }
  if (!input.cannon) {
    return fail('cannon', 'slot_missing');
  }

  const lookup = input.lookup ?? findItemDefinition;
  const shipCommon = validateCommon('ship', input.accountId, input.ship, lookup);
  if (isResolution(shipCommon)) {
    return shipCommon;
  }
  if (shipCommon.definition.category !== 'ship') {
    return fail('ship', 'wrong_category');
  }
  const shipMeta = shipCommon.definition.metadata;
  if (shipMeta?.kind !== 'ship') {
    // A ship definition without its catalog link cannot be rendered or scored.
    return fail('ship', 'definition_unknown');
  }
  // Recomputed from the catalog, never read from a persisted column.
  const catalogShip = shipCatalogEntry(shipMeta.shipIndex);
  const damageBonusBps = catalogShip.level1DamageBonusBps * 2 ** (input.ship.level - 1);

  const cannonCommon = validateCommon('cannon', input.accountId, input.cannon, lookup);
  if (isResolution(cannonCommon)) {
    return cannonCommon;
  }
  if (cannonCommon.definition.category !== 'cannon') {
    return fail('cannon', 'wrong_category');
  }
  if (!isCannonRarity(cannonCommon.definition.rarity)) {
    // Cannons exist only in the top four rarities; anything else is a row that
    // should not exist, and it must not become a playable configuration.
    return fail('cannon', 'invalid_cannon_rarity');
  }
  const catalogCannon = CANNON_CATALOG[cannonCommon.definition.rarity];

  /*
   * The skin, LAST, and fail-open.
   *
   * Last because everything above is already decided: the damage bonus, the
   * cannon count and the projectiles per cannon are computed before this line
   * runs and none of them can see it. A skin resolved after them is a skin that
   * cannot have changed any of them - the structural half of "a skin moves
   * nothing", stated as an order rather than as a rule to remember.
   *
   * Fail-open because the worst honest outcome of a broken cosmetic is the
   * normal hull. Every reason a ship would be REFUSED for - foreign owner, not
   * owned, reserved, unknown definition, disabled definition - becomes `null`
   * here instead, so a corrupt skin row can never stop a run from starting. And
   * `null` is the same answer an account that owns nothing gets, so there is no
   * second "broken" state for anything downstream to draw.
   */
  const skin = resolveWornSkin(input.accountId, input.skin ?? null, lookup);

  return {
    ok: true,
    loadout: {
      ship: {
        instanceId: input.ship.instanceId,
        definitionId: shipCommon.definition.id,
        shipIndex: shipMeta.shipIndex,
        rarity: shipCommon.definition.rarity,
        level: input.ship.level,
        damageBonusBps,
        contentVersion: shipCommon.definition.contentVersion,
        primordialHeartCount: (input.primordialHeartCount ?? 0n).toString(10),
      },
      jewels: input.jewels ?? EMPTY_JEWEL_SOCKET_PROFILE,
      cannon: {
        instanceId: input.cannon.instanceId,
        definitionId: cannonCommon.definition.id,
        rarity: cannonCommon.definition.rarity,
        level: input.cannon.level,
        cannonCount: cannonCountForLoadoutLevel(input.cannon.level),
        projectilesPerCannon: catalogCannon.projectilesPerCannon,
        contentVersion: cannonCommon.definition.contentVersion,
      },
      skin,
    },
  };
}

/**
 * The worn skin as presentation identity, or null for every other outcome.
 *
 * Deliberately not `validateCommon`. That helper returns a typed FAILURE, and a
 * failure is the one thing that must not happen for a cosmetic. This mirrors
 * its checks and answers `null`, so the two cannot drift into disagreeing about
 * what a usable instance is while still disagreeing about the consequence.
 */
function resolveWornSkin(
  accountId: string,
  input: EquippedInstanceInput | null,
  lookup: (definitionId: string) => ItemDefinition | null,
): ResolvedSkin | null {
  if (!input) {
    return null;
  }
  if (input.ownerAccountId !== accountId || input.state !== 'owned' || input.reserved) {
    return null;
  }
  const definition = lookup(input.definitionId);
  if (definition?.category !== 'skin') {
    return null;
  }
  /*
   * A DISABLED definition draws nothing, and that is the production guard.
   *
   * The three official skins are draft and disabled, so this is the line that
   * keeps unfinished content out of a real hunt even if a row for one existed.
   * The E2E harness reaches them through its own catalogue, never through here.
   */
  if (!definition.enabled) {
    return null;
  }
  return { instanceId: input.instanceId, definitionId: definition.id };
}

/* -------------------------------------------------------------------------- */
/*  Persistence and wire shape                                                */
/* -------------------------------------------------------------------------- */

/**
 * Validator for a resolved loadout read back out of a session row.
 *
 * A session's loadout is JSON in PostgreSQL, so it is parsed before it is
 * trusted - exactly like the hunt snapshot. `cannonRarity` is narrowed here,
 * so a row holding a Common cannon cannot be restored into a run.
 */
export const resolvedShipSchema = z.object({
  instanceId: z.uuid(),
  definitionId: itemDefinitionIdSchema,
  shipIndex: z.number().int().min(1),
  rarity: raritySchema,
  level: z.number().int().min(1),
  damageBonusBps: z.number().int().min(0),
  contentVersion: z.number().int().min(1),
  /**
   * Corações Primordiais incorporados NESTA cópia (gate V28F.B).
   *
   * String decimal, e sem tecto: a contagem vive em `numeric(40, 0)` e um
   * `number` perderia dígitos acima de 2^53 - a mesma regra dos saldos. O 80 é
   * um limite de PAYLOAD, o dobro da capacidade de armazenamento, e não um
   * tecto de jogo.
   *
   * `.default('0')` porque toda a sessão congelada antes disto existir não a
   * tem, e a ausência é EXATA: essas corridas foram jogadas sem corações. Uma
   * sessão em curso não pode deixar de ser legível por causa de um campo novo.
   *
   * Congelada com o resto do loadout, e é isso que a faz certa: um checkpoint
   * re-simula a janela com os corações que a nave tinha durante ela, e um
   * coração incorporado depois nunca paga retroativamente.
   */
  primordialHeartCount: z
    .string()
    .regex(/^(0|[1-9][0-9]*)$/, 'must be a canonical non-negative decimal')
    .max(80)
    .default('0'),
});

export const resolvedCannonSchema = z.object({
  instanceId: z.uuid(),
  definitionId: itemDefinitionIdSchema,
  rarity: cannonRaritySchema,
  level: z.number().int().min(1),
  cannonCount: z.number().int().min(1),
  projectilesPerCannon: z.number().int().min(1),
  contentVersion: z.number().int().min(1),
});

/**
 * The worn skin, or nothing. Presentation identity only (gate V23.1B).
 *
 * ## Two fields, and why neither is more
 *
 * `definitionId` is a **content** identity: the client looks the descriptor up
 * in its own validated manifest and refuses anything it cannot find there. The
 * wire never carries an asset key, a file path or a descriptor, because a
 * client that accepted one would be drawing whatever the sender named.
 *
 * `instanceId` is opaque and exists only so a caller can say WHICH copy is on -
 * two copies of one skin draw the same art and are different economic objects.
 * Nothing visual is derived from it.
 *
 * ## What is deliberately absent
 *
 * No `socketCapacity`, no seed, no rolled point, no roulette version, no banner,
 * no origin, no Cash, no jewel and no attribute. A skin's capacity belongs to
 * the copy and to the wardrobe; the hunt has no use for it, and a renderer that
 * received one would be a renderer that could one day read it.
 */
export const resolvedSkinSchema = z.object({
  instanceId: z.uuid(),
  definitionId: itemDefinitionIdSchema,
});

export const resolvedLoadoutSchema = z.object({
  ship: resolvedShipSchema,
  cannon: resolvedCannonSchema,
  /**
   * As joias encaixadas na skin equipada, agregadas (gate V28G.A).
   *
   * ## Irmão do loadout, e não filho da skin
   *
   * Porque um perfil é uma propriedade do CASCO, não da arte. `resolvedSkin`
   * carrega identidade de apresentação e nada mais - sem capacidade, sem socket,
   * sem joia - e essa fronteira não foi movida por este gate. Uma corrida sem
   * skin tem o perfil vazio pela via natural: sem skin não há assentos.
   *
   * ## `.default` e o que a ausência significa
   *
   * Toda a sessão congelada antes disto existir não tem o campo, e a ausência é
   * EXATA: essas corridas foram jogadas sem joias, porque nenhum socket podia
   * conter alguma coisa. Uma sessão em curso não pode deixar de ser legível por
   * causa de um campo novo.
   *
   * Um perfil PRESENTE e impossível é outra coisa e é recusado: um campo em
   * falta tem um significado, um campo corrompido não tem nenhum, e
   * substituí-lo pelo vazio daria ao jogador um casco mais fraco do que a sessão
   * registou sem que ninguém soubesse.
   *
   * ## Congelado com o resto, e é isso que o faz certo
   *
   * Um checkpoint re-simula a janela com as joias que a nave tinha DURANTE ela.
   * Uma joia encaixada depois nunca paga retroativamente, e a mesma sessão passa
   * a usá-la a partir da fronteira em que o loadout é reaplicado.
   */
  jewels: jewelSocketProfileSchema.default(() => ({ entries: [] })),
  /**
   * Absent on every row written before skins reached the hunt.
   *
   * `.default(null)` rather than `.optional()`: a session persisted last month
   * parses into an explicit "no skin" instead of into `undefined`, so every
   * reader downstream sees one shape. This contract carries no version and its
   * change policy is additive, which this honours in both directions - an old
   * server's answer parses here, and an old client ignores the new key.
   */
  skin: resolvedSkinSchema.nullable().default(null),
});

export type ResolvedLoadoutWire = z.infer<typeof resolvedLoadoutSchema>;

/** Parses a persisted loadout, throwing rather than guessing. */
export function parseResolvedLoadout(value: unknown): ResolvedLoadoutWire {
  return resolvedLoadoutSchema.parse(value);
}
